import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react'
import { updateThemePreference } from '../lib/userService'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: (userId?: string) => void
  setThemeFromFirestore: (themePreference: number) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('wonderstood-theme') as Theme | null
    return savedTheme || 'dark'
  })

  const isTogglingRef = useRef(false)

  // Update localStorage and document class when theme changes
  useEffect(() => {
    localStorage.setItem('wonderstood-theme', theme)

    // Add/remove dark class on document root for Tailwind dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  const toggleTheme = async (userId?: string) => {
    if (isTogglingRef.current) {
      return
    }

    isTogglingRef.current = true

    // Calculate new theme outside of setState to avoid double execution
    const newTheme = theme === 'light' ? 'dark' : 'light'

    // Save to Firestore if user is logged in
    if (userId) {
      const themePreference = newTheme === 'dark' ? 1 : 0
      updateThemePreference(userId, themePreference).catch(err => {
        console.error('Failed to save theme preference to Firestore:', err)
      })
    }

    setTheme(newTheme)

    // Reset the flag after a short delay
    setTimeout(() => {
      isTogglingRef.current = false
    }, 100)
  }

  const setThemeFromFirestore = (themePreference: number) => {
    const newTheme: Theme = themePreference === 1 ? 'dark' : 'light'
    setTheme(newTheme)
    // Also update localStorage for consistency
    localStorage.setItem('wonderstood-theme', newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setThemeFromFirestore }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
